import type { ProspectGroupPortalData } from '../../services/tenantApi';

export interface ClosingMember {
  id: string;
  displayName: string;
  isViewer: boolean;
  headshotDocumentId: string | null;
}

export interface ClosingPersonalState {
  applicantId: string | null;
  headshotDocumentId: string | null;
  padFileName: string | null;
  padDocumentId: string | null;
  /** DocuSign completion. The sign button does not set this until that flow exists. */
  padSigned: boolean;
  voidChequeFileName: string | null;
  voidChequeDocumentId: string | null;
  /** Screenshot of the e-transfer confirmation; uploading it marks the deposit as sent. */
  lastMonthRentETransferFileName: string | null;
  lastMonthRentETransferDocumentId: string | null;
  /** Tenant checked off last month's rent e-transfer sent. */
  lastMonthRentETransferSent: boolean;
  /** Staff confirmed last month's rent e-transfer received. */
  lastMonthRentETransferReceived: boolean;
  /** Shown on the viewer's own tab only. */
  eTransferAmountLabel: string | null;
  padReview: ClosingReviewState;
  voidChequeReview: ClosingReviewState;
  eTransferSentReview: ClosingReviewState;
  eTransferReceivedReview: ClosingReviewState;
}

export type ClosingReviewStatus = 'PENDING' | 'APPROVED' | 'REVIEW_FEEDBACK';

export interface ClosingReviewState {
  status: ClosingReviewStatus;
  latestFeedbackMessage: string | null;
}

export interface UtilitiesKingstonSetupContext {
  tenantDisplayName: string;
  propertyAddress: string | null;
  leaseStartDateLabel: string | null;
  utilityServiceLabels: string[];
}

export function emptyUtilitiesKingstonSetupContext(): UtilitiesKingstonSetupContext {
  return {
    tenantDisplayName: 'your name',
    propertyAddress: null,
    leaseStartDateLabel: null,
    utilityServiceLabels: ['water', 'wastewater', 'natural gas', 'electricity'],
  };
}

export interface LeaseSigningSigner {
  name: string;
  signed: boolean;
  role?: 'tenant' | 'amberPeak';
  personId?: string | null;
}

export interface ClosingGroupState {
  leaseSent: boolean;
  signedByPersonId: Record<string, boolean>;
  /** Tenants, then Amber Peak. The reviewer is not included. */
  leaseSigners: LeaseSigningSigner[];
  leaseFileName: string | null;
  leaseDocumentId: string | null;
  utilityFileName: string | null;
  utilityDocumentId: string | null;
  utilitySetupReview: ClosingReviewState;
  utilitiesKingston: UtilitiesKingstonSetupContext;
  housemateAgreementReview: ClosingReviewState;
  housemateTemplateReady: boolean;
  housemateSignedFileName: string | null;
  housemateDocumentId: string | null;
}

export interface ClosingChecklist {
  selected: boolean;
  members: ClosingMember[];
  personal: Record<string, ClosingPersonalState>;
  group: ClosingGroupState;
}

export interface ClosingProgress {
  completed: number;
  total: number;
}

const ACTIVE_MEMBER_STATUSES = new Set(['ACCEPTED', 'INVITED']);
const CLOSING_DOCUMENT_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif', 'bmp', 'tif', 'tiff']);
const CLOSING_DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/tiff',
]);
const BLOCKED_EXTENSIONS = new Set([
  'js', 'mjs', 'cjs', 'exe', 'bat', 'cmd', 'com', 'scr', 'msi', 'dll', 'app', 'dmg',
  'sh', 'bash', 'ps1', 'php', 'html', 'htm', 'svg', 'xml', 'jar', 'vbs', 'wsf', 'zip', 'rar',
]);

/** Same file types as the government ID upload on Apply to a Unit. */
export const CLOSING_DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.bmp,.tif,.tiff,application/pdf,image/jpeg,image/png,image/webp,image/gif,image/heic,image/bmp,image/tiff';
export const CLOSING_DOCUMENT_HINT = 'PDF, JPEG, PNG, WebP, GIF, HEIC, BMP, or TIFF.';
const PREVIEW_ROOMMATE_ID = 'preview-roommate';

export function emptyPersonal(): ClosingPersonalState {
  return {
    applicantId: null,
    headshotDocumentId: null,
    padFileName: null,
    padDocumentId: null,
    padSigned: false,
    voidChequeFileName: null,
    voidChequeDocumentId: null,
    lastMonthRentETransferFileName: null,
    lastMonthRentETransferDocumentId: null,
    lastMonthRentETransferSent: false,
    lastMonthRentETransferReceived: false,
    eTransferAmountLabel: null,
    padReview: emptyClosingReviewState(),
    voidChequeReview: emptyClosingReviewState(),
    eTransferSentReview: emptyClosingReviewState(),
    eTransferReceivedReview: emptyClosingReviewState(),
  };
}

export function emptyClosingReviewState(): ClosingReviewState {
  return { status: 'PENDING', latestFeedbackMessage: null };
}

export function emptyGroupState(): ClosingGroupState {
  return {
    leaseSent: false,
    signedByPersonId: {},
    leaseSigners: [],
    leaseFileName: null,
    leaseDocumentId: null,
    utilityFileName: null,
    utilityDocumentId: null,
    utilitySetupReview: emptyClosingReviewState(),
    utilitiesKingston: emptyUtilitiesKingstonSetupContext(),
    housemateAgreementReview: emptyClosingReviewState(),
    housemateTemplateReady: false,
    housemateSignedFileName: null,
    housemateDocumentId: null,
  };
}

/** Uploaded and not waiting on a tenant re-upload after staff feedback. */
export function utilitySetupStepComplete(group: ClosingGroupState): boolean {
  if (!group.utilityFileName) return false;
  return group.utilitySetupReview.status !== 'REVIEW_FEEDBACK';
}

export function housemateStepComplete(group: ClosingGroupState): boolean {
  if (!group.housemateSignedFileName) return false;
  return group.housemateAgreementReview.status !== 'REVIEW_FEEDBACK';
}

export function applyLeaseSigningStatus(
  group: ClosingGroupState,
  status: { leaseSent: boolean; signedPersonIds: string[]; signers?: LeaseSigningSigner[] } | null,
): ClosingGroupState {
  if (!status) return group;
  const signedByPersonId: Record<string, boolean> = {};
  status.signedPersonIds.forEach((personId) => {
    signedByPersonId[personId] = true;
  });
  return {
    ...group,
    leaseSent: status.leaseSent,
    signedByPersonId,
    leaseSigners: status.signers ?? [],
  };
}

export function personalOrEmpty(
  personal: Record<string, ClosingPersonalState>,
  personId: string,
): ClosingPersonalState {
  return personal[personId] ?? emptyPersonal();
}

export function padIsComplete(state: ClosingPersonalState): boolean {
  if (state.padSigned) return true;
  if (!state.padFileName) return false;
  return state.padReview.status !== 'REVIEW_FEEDBACK';
}

export function voidChequeIsComplete(state: ClosingPersonalState): boolean {
  if (!state.voidChequeFileName) return false;
  return state.voidChequeReview.status !== 'REVIEW_FEEDBACK';
}

export function eTransferIsComplete(state: ClosingPersonalState): boolean {
  return state.lastMonthRentETransferSent && state.lastMonthRentETransferReceived;
}

export function personStepProgress(state: ClosingPersonalState): ClosingProgress {
  const completed = [padIsComplete(state), voidChequeIsComplete(state), eTransferIsComplete(state)]
    .filter(Boolean).length;
  return { completed, total: 3 };
}

export function leaseStepComplete(members: ClosingMember[], group: ClosingGroupState): boolean {
  if (!group.leaseSent) return false;
  if (group.leaseSigners.length > 0) return group.leaseSigners.every((signer) => signer.signed);
  if (members.length === 0) return false;
  return members.every((member) => group.signedByPersonId[member.id] === true);
}

/**
 * Housemate agreement stays in the checklist API. Flip this when the template
 * is ready to show on the closing page again.
 */
export const SHOW_HOUSEMATE_AGREEMENT_STEP = false;

export function groupStepProgress(members: ClosingMember[], group: ClosingGroupState): ClosingProgress {
  const completed = [
    leaseStepComplete(members, group),
    utilitySetupStepComplete(group),
    SHOW_HOUSEMATE_AGREEMENT_STEP && housemateStepComplete(group),
  ].filter(Boolean).length;
  return { completed, total: SHOW_HOUSEMATE_AGREEMENT_STEP ? 3 : 2 };
}

/** Header total: every person's three steps, plus the visible group steps. */
export function groupClosingProgress(checklist: ClosingChecklist): ClosingProgress {
  if (!checklist.selected || checklist.members.length === 0) return { completed: 0, total: 1 };
  let completed = 0;
  let total = 0;
  for (const member of checklist.members) {
    const step = personStepProgress(personalOrEmpty(checklist.personal, member.id));
    completed += step.completed;
    total += step.total;
  }
  const group = groupStepProgress(checklist.members, checklist.group);
  return { completed: completed + group.completed, total: total + group.total };
}

/**
 * Sidebar ring: the viewer's three steps plus the three group steps.
 * Stays empty until the group is selected for a home.
 */
export function viewerClosingProgress(checklist: ClosingChecklist | null | undefined): ClosingProgress {
  if (!checklist?.selected) return { completed: 0, total: 1 };
  const viewer = checklist.members.find((member) => member.isViewer);
  const person = viewer
    ? personStepProgress(personalOrEmpty(checklist.personal, viewer.id))
    : { completed: 0, total: 3 };
  const group = groupStepProgress(checklist.members, checklist.group);
  return { completed: person.completed + group.completed, total: person.total + group.total };
}

export function closingPercent(progress: ClosingProgress): number {
  if (progress.total <= 0) return 0;
  return Math.round((progress.completed / progress.total) * 100);
}

export function orderClosingMembers(members: ClosingMember[]): ClosingMember[] {
  return [
    ...members.filter((member) => member.isViewer),
    ...members.filter((member) => !member.isViewer),
  ];
}

export function membersFromGroup(
  group: ProspectGroupPortalData | null,
  viewerPersonId: string | null,
  viewerName: string,
): ClosingMember[] {
  const fromGroup = (group?.members ?? [])
    .filter((member) => ACTIVE_MEMBER_STATUSES.has(member.status))
    .map((member) => ({
      id: member.personId,
      displayName: member.displayName.trim() || 'Group member',
      isViewer: viewerPersonId != null && member.personId === viewerPersonId,
      headshotDocumentId: null,
    }));

  if (fromGroup.length === 0) {
    if (!viewerPersonId) return [];
    return [{ id: viewerPersonId, displayName: viewerName, isViewer: true, headshotDocumentId: null }];
  }
  if (fromGroup.some((member) => member.isViewer) || !viewerPersonId) {
    return orderClosingMembers(fromGroup);
  }
  return orderClosingMembers([
    { id: viewerPersonId, displayName: viewerName, isViewer: true },
    ...fromGroup,
  ]);
}

/** Preview needs a second person so the other-person tab can be opened. */
export function withPreviewRoster(members: ClosingMember[]): ClosingMember[] {
  if (members.some((member) => !member.isViewer)) return members;
  const viewer = members.find((member) => member.isViewer) ?? members[0] ?? null;
  const roster = viewer ? [viewer] : [{ id: 'preview-you', displayName: 'You', isViewer: true, headshotDocumentId: null }];
  return [
    ...roster,
    { id: PREVIEW_ROOMMATE_ID, displayName: 'Alex Morgan', isViewer: false, headshotDocumentId: null },
  ];
}

export function samplePersonal(members: ClosingMember[]): Record<string, ClosingPersonalState> {
  const personal: Record<string, ClosingPersonalState> = {};
  for (const member of members) {
    personal[member.id] = member.isViewer
      ? {
          ...emptyPersonal(),
          padFileName: 'pad-agreement.pdf',
          eTransferAmountLabel: '$1,450.00',
        }
      : {
          ...emptyPersonal(),
          padSigned: true,
          voidChequeFileName: 'void-cheque.pdf',
          lastMonthRentETransferFileName: 'e-transfer-confirmation.png',
          lastMonthRentETransferSent: true,
          lastMonthRentETransferReceived: true,
          eTransferAmountLabel: '$1,450.00',
        };
  }
  return personal;
}

export function sampleGroup(members: ClosingMember[]): ClosingGroupState {
  const signedByPersonId: Record<string, boolean> = {};
  let firstOtherUnsigned = true;
  for (const member of members) {
    if (member.isViewer) {
      signedByPersonId[member.id] = true;
      continue;
    }
    signedByPersonId[member.id] = !firstOtherUnsigned;
    firstOtherUnsigned = false;
  }
  return {
    leaseSent: true,
    signedByPersonId,
    leaseSigners: [],
    leaseFileName: null,
    leaseDocumentId: null,
    utilityFileName: 'utilities-confirmation.pdf',
    utilityDocumentId: null,
    utilitySetupReview: { status: 'PENDING', latestFeedbackMessage: null },
    utilitiesKingston: {
      tenantDisplayName: members.find((m) => m.isViewer)?.displayName ?? 'Alex Sample',
      propertyAddress: '#201 · 390 Johnson Street',
      leaseStartDateLabel: 'September 1, 2026',
      utilityServiceLabels: ['water', 'wastewater', 'natural gas', 'electricity'],
    },
    housemateAgreementReview: { status: 'PENDING', latestFeedbackMessage: null },
    housemateTemplateReady: true,
    housemateSignedFileName: null,
    housemateDocumentId: null,
  };
}

export function closingFileProblem(file: File): string | null {
  const parts = file.name.toLowerCase().split(/[/\\]/).pop()?.split('.') ?? [];
  const extensions = parts.slice(1);
  const extension = extensions[extensions.length - 1] ?? '';
  if (!extension || extensions.some((part) => BLOCKED_EXTENSIONS.has(part))) {
    return 'Scripts and other executable files are not accepted.';
  }
  if (!CLOSING_DOCUMENT_EXTENSIONS.has(extension) || (file.type && !CLOSING_DOCUMENT_MIME_TYPES.has(file.type))) {
    return 'Use a PDF or a photo: JPEG, PNG, WebP, GIF, HEIC, BMP, or TIFF.';
  }
  return null;
}

export function downloadHousematePlaceholder(): void {
  const blob = new Blob(
    ['Housemate agreement. Sign this copy, then upload it on the Closing page.\n'],
    { type: 'text/plain' },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'housemate-agreement.txt';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
